import { useMemo, useState } from "react";
import {
  EDITIONS,
  editionOutcome,
  formatDayShort,
  formatPrice,
} from "@/utils/race-results";
import { Trophy } from "lucide-react";
import { Cloth, DayStatus, EmptyState, ProBand } from "./parts";

const PAGE = 24;

const SELECT =
  "h-8 rounded-lg border border-gray-200 bg-white px-2 text-[13px] focus:border-brand-pink focus:outline-none";

/** A card for every race a Lexi Top Tip won (or tips 1–4), with an honest count. */
export default function WinnersGrid({ days }) {
  const [sort, setSort] = useState("new");
  const [includeAll, setIncludeAll] = useState(false);
  const [limit, setLimit] = useState(PAGE);

  const loaded = days.filter((d) => d.status === "success");
  const waiting = days.filter((d) => d.status !== "success");

  const { winners, total } = useMemo(() => {
    const races = days
      .filter((d) => d.status === "success")
      .flatMap((d) => d.races);
    const hits = (race) =>
      EDITIONS.filter(({ key }) => {
        const { kind } = editionOutcome(race, key);
        return kind === "top" || (includeAll && kind === "tip");
      });
    const list = races
      .map((race) => ({ race, hits: hits(race) }))
      .filter((w) => w.hits.length);
    list.sort((a, b) =>
      sort === "price"
        ? (b.race.winner.sp || 0) - (a.race.winner.sp || 0)
        : b.race.date.localeCompare(a.race.date) ||
          String(b.race.start || "").localeCompare(String(a.race.start || "")),
    );
    return { winners: list, total: races.length };
  }, [days, sort, includeAll]);

  const cards = winners.slice(0, limit).map(({ race, hits }) => (
    <article
      key={race.id}
      className="grid min-w-0 content-start gap-2 rounded-xl border border-gray-100 bg-white p-3.5 shadow-lg shadow-black/[0.04] transition-colors hover:border-brand-pink/20"
    >
      <div className="flex justify-between gap-2 text-[12.5px] text-gray-500">
        <span className="min-w-0 truncate">
          <b className="font-bold text-gray-900">
            {race.track} R{race.race}
          </b>
          {race.state && ` · ${race.state}`}
        </span>
        <span className="shrink-0">{formatDayShort(race.date)}</span>
      </div>
      <div className="flex items-center gap-2 text-base font-extrabold leading-tight tracking-tight sm:text-lg">
        <Cloth no={race.winner.no} />
        <span className="min-w-0">{race.winner.name}</span>
      </div>
      <div className="flex items-center gap-1.5 text-[24px] font-black tabular-nums tracking-tight text-brand-pink sm:text-[26px]">
        <Trophy className="h-5 w-5" aria-hidden="true" />
        {formatPrice(race.winner.sp)}
        <small className="text-[11.5px] font-semibold tracking-normal text-gray-500">
          Betfair SP
        </small>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {hits.map(({ key, label }) => {
          const { kind, n } = editionOutcome(race, key);
          return kind === "top" ? (
            <span
              key={key}
              className="rounded-md bg-brand-pink px-2 py-0.5 text-xs font-semibold text-white"
            >
              {label} Top Tip
            </span>
          ) : (
            <span
              key={key}
              className="rounded-md border border-pink-200 bg-[#FFE6F6] px-2 py-0.5 text-xs font-semibold text-[#C8007E]"
            >
              {label} tip {n}
            </span>
          );
        })}
      </div>
    </article>
  ));
  if (cards.length)
    cards.splice(Math.min(5, cards.length), 0, <ProBand key="pro" compact />);

  return (
    <div className="grid gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3 text-sm tabular-nums text-gray-600">
        <span>
          <b className="text-base text-gray-900">{winners.length} winners</b>{" "}
          from {total} races
          {total ? ` (${Math.round((100 * winners.length) / total)}%)` : ""}
        </span>
        <span className="flex gap-2">
          <select
            id="race-results-sort"
            aria-label="Sort winners"
            className={SELECT}
            value={sort}
            onChange={(e) => {
              setSort(e.target.value);
              setLimit(PAGE);
            }}
          >
            <option value="new">Newest first</option>
            <option value="price">Biggest price first</option>
          </select>
          <select
            id="race-results-include"
            aria-label="Which tips count"
            className={SELECT}
            value={includeAll ? "all" : "top"}
            onChange={(e) => {
              setIncludeAll(e.target.value === "all");
              setLimit(PAGE);
            }}
          >
            <option value="top">Top Tips only</option>
            <option value="all">Tips 1–4</option>
          </select>
        </span>
      </div>

      {waiting.map((day) =>
        day.status === "error" ? (
          <DayStatus key={day.iso} day={day} label={formatDayShort(day.iso)} />
        ) : null,
      )}

      {cards.length ? (
        <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-[repeat(auto-fill,minmax(230px,1fr))] sm:gap-3">
          {cards}
        </div>
      ) : loaded.length ? (
        <EmptyState>No winners in this selection.</EmptyState>
      ) : null}

      {waiting.some((d) => d.status === "pending") && (
        <p className="text-sm text-gray-500">
          Loading {waiting.filter((d) => d.status === "pending").length} more
          days…
        </p>
      )}

      {winners.length > limit && (
        <button
          type="button"
          onClick={() => setLimit((n) => n + PAGE)}
          className="justify-self-start rounded-lg border border-gray-200 px-3.5 py-2 text-[13.5px] font-semibold hover:border-[#F0A7D5] hover:text-[#C8007E]"
        >
          Show more winners ({winners.length - limit} left)
        </button>
      )}
    </div>
  );
}
